import * as React from 'react';
import { Check, Clock, Eye, Send } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { StatusChip } from '@/components/ui/status-chip';
import {
  CodeBadge,
  DetailHead,
  RecordRow,
  WorkspaceButton,
  WorkspaceSelect,
  useToast,
} from '@/components/ui/workspace';
import {
  remindRound,
  sendRound,
  setRoundContact,
  useDiscoveryWrite,
  usePeopleQuery,
  useRoundQuery,
  type RoundDepartment,
} from '@/lib/api/discovery-sessions';
import { plural } from '../discovery-logic';
import { errorText } from './errors';
import { ExtendLinkButton, QuestionnairePreview, useLinkDays } from './link-bits';
import { longDay } from './sessions-logic';

/**
 * The department questionnaire round (E12-F08, Decision 53.10): one named
 * contact per department, one response per department, by secure link.
 * Send to the departments not sent yet, remind the ones that have not
 * answered, extend a link, and preview what a contact sees.
 * Prototype: `roundDetail` (`#sessions/QG`).
 */

const SKIP_WORDS: Record<string, string> = {
  no_contact: 'no contact yet',
  no_email: 'the contact has no email',
  already_sent: 'already sent',
  contact_retired: 'the contact is retired',
  contact_merged: 'the contact was merged',
};

export function RoundDetail({
  engagementId,
  roundId,
  canExtend,
}: {
  engagementId: string;
  roundId: string;
  canExtend: boolean;
}) {
  const { toast } = useToast();
  const q = useRoundQuery(engagementId, roundId);
  const days = useLinkDays();
  const [preview, setPreview] = React.useState(false);
  const send = useDiscoveryWrite(engagementId, () => sendRound(engagementId, roundId));
  const remind = useDiscoveryWrite(engagementId, (ids: string[]) =>
    remindRound(engagementId, roundId, ids),
  );
  const fail = (e: unknown) => toast({ message: errorText(e), tone: 'critical' });

  if (q.error) return <ErrorMessage error={q.error} />;
  if (!q.data) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading the department round…
      </div>
    );
  }
  const r = q.data;
  const notSent = r.departments.filter((x) => x.status === 'not_sent');
  const waiting = r.departments.filter((x) => x.status === 'sent');
  const sendable = notSent.filter((x) => x.contact?.email);
  const nameOf = (id: string) => r.departments.find((x) => x.id === id)?.department ?? '';

  return (
    <div>
      <DetailHead
        leading={<CodeBadge size="lg">{r.questionnaire.code}</CodeBadge>}
        title={r.questionnaire.name}
        meta={`One response per department, by secure link · about ${r.questionnaire.minutes} minutes · ${plural(r.questionnaire.questions, 'question')}${r.sent_at ? ` · sent ${longDay(r.sent_at)}` : ''}`}
        trailing={
          <StatusChip tone="amber">
            {r.answered} of {r.total}
          </StatusChip>
        }
      />
      <div>
        {r.departments.map((x) => (
          <DepartmentRow
            key={x.id}
            engagementId={engagementId}
            roundId={roundId}
            row={x}
            canExtend={canExtend}
          />
        ))}
      </div>
      <div className="mt-1.5 text-meta text-ink-500">
        Links last {days} days, set in Firm settings. Only the engagement lead can extend one.
      </div>
      <div className="mt-3.5 flex flex-wrap gap-2">
        {sendable.length > 0 ? (
          <WorkspaceButton
            variant="primary"
            size="md"
            disabled={send.isPending}
            onClick={() =>
              send.mutate(undefined, {
                onSuccess: (res) => {
                  const skipped = res.skipped.filter((s) => s.reason !== 'already_sent');
                  toast({
                    message: `The department questionnaire went to ${plural(res.sent.length, 'department')} by secure link. Answers count as evidence from each person who replies.${
                      skipped.length > 0
                        ? ` Not sent: ${skipped.map((s) => `${nameOf(s.department_id)} (${SKIP_WORDS[s.reason] ?? s.reason})`).join(', ')}.`
                        : ''
                    }`,
                  });
                },
                onError: fail,
              })
            }
          >
            <Send className="h-3.5 w-3.5" aria-hidden="true" /> Send to the{' '}
            {plural(sendable.length, 'department')} not sent yet
          </WorkspaceButton>
        ) : null}
        {waiting.length > 0 ? (
          <WorkspaceButton
            size="md"
            disabled={remind.isPending}
            onClick={() =>
              remind.mutate(
                waiting.map((x) => x.id),
                {
                  onSuccess: (res) =>
                    toast({
                      message: `Reminder sent to ${res.reminded.map(nameOf).join(', ') || 'nobody'}, with the same links.`,
                    }),
                  onError: fail,
                },
              )
            }
          >
            <Send className="h-3.5 w-3.5" aria-hidden="true" /> Remind the {waiting.length} that
            haven’t answered
          </WorkspaceButton>
        ) : null}
        <WorkspaceButton
          variant="tertiary"
          size="md"
          aria-expanded={preview}
          onClick={() => setPreview((v) => !v)}
        >
          <Eye className="h-3.5 w-3.5" aria-hidden="true" /> {preview ? 'Hide preview' : 'Preview'}
        </WorkspaceButton>
      </div>
      {preview ? (
        <QuestionnairePreview
          engagementId={engagementId}
          questionnaireId={r.questionnaire.id}
          department={r.departments[0]?.department ?? ''}
        />
      ) : null}
    </div>
  );
}

function DepartmentRow({
  engagementId,
  roundId,
  row: x,
  canExtend,
}: {
  engagementId: string;
  roundId: string;
  row: RoundDepartment;
  canExtend: boolean;
}) {
  const answered = x.status === 'answered';
  return (
    <RecordRow
      muted={!answered}
      icon={answered ? <Check className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
    >
      <span className="min-w-0 flex-1 text-ink-900">{x.department}</span>
      {answered ? (
        <span className="text-meta text-ink-500">
          Answered by {x.answered_by?.full_name ?? x.contact?.full_name}
        </span>
      ) : x.status === 'sent' ? (
        <>
          <span className="text-meta text-ink-500">
            {x.contact?.full_name} · not answered yet
            {x.link ? ` · link expires ${longDay(x.link.expires_at)}` : ''}
          </span>
          {x.link && x.link.status === 'live' ? (
            <ExtendLinkButton
              engagementId={engagementId}
              link={x.link}
              canExtend={canExtend}
              who={x.contact?.full_name ?? x.department}
            />
          ) : null}
        </>
      ) : (
        <ContactPicker engagementId={engagementId} roundId={roundId} row={x} />
      )}
    </RecordRow>
  );
}

/** Before a department is sent, its contact can be named or changed (Decision 53.10). */
function ContactPicker({
  engagementId,
  roundId,
  row: x,
}: {
  engagementId: string;
  roundId: string;
  row: RoundDepartment;
}) {
  const { toast } = useToast();
  const people = usePeopleQuery(engagementId);
  const set = useDiscoveryWrite(engagementId, (personId: string) =>
    setRoundContact(engagementId, roundId, x.id, personId),
  );
  const options = (people.data?.items ?? []).filter(
    (p) => p.status !== 'retired' && !p.possible_duplicate_of,
  );
  return (
    <span className="flex items-center gap-2 text-meta text-ink-500">
      {x.contact ? (
        <span>
          {x.contact.full_name} · not sent yet{x.contact.email ? '' : ' · no email'}
        </span>
      ) : (
        <span>No contact yet</span>
      )}
      <WorkspaceSelect
        aria-label={`Contact for ${x.department}`}
        className="h-[30px] w-auto py-0 text-meta"
        value=""
        disabled={set.isPending}
        onChange={(e) => {
          if (!e.target.value) return;
          set.mutate(e.target.value, {
            onError: (err) => toast({ message: errorText(err), tone: 'critical' }),
          });
        }}
      >
        <option value="">{x.contact ? 'Change contact' : 'Pick a contact'}</option>
        {options.map((p) => (
          <option key={p.id} value={p.id}>
            {p.full_name}
            {p.title ? `, ${p.title}` : ''}
          </option>
        ))}
      </WorkspaceSelect>
    </span>
  );
}
